import type {
  ClaimElement,
  ConceptElement,
  DomainElement,
  PaperElement,
  PhenomenonElement,
  SystemElement,
} from '../data/types';
import { BY_ID } from '../data/system';

const SLUG_BY_KIND: Record<string, string> = {
  concept: 'concepts',
  claim: 'claims',
  phenomenon: 'phenomena',
  paper: 'papers',
  domain: 'domains',
  feature: '',
};

function eyebrowFor(el: SystemElement): string {
  switch (el.kind) {
    case 'concept': {
      const c = el as ConceptElement;
      const map = {
        foundational: 'Foundational Concept',
        formal: 'Formal Apparatus',
        diagnostic: 'Diagnostic Concept',
        derived: 'Derived Concept',
      } as const;
      return map[c.group];
    }
    case 'claim':
      return (el as ClaimElement).status === 'foundational' ? 'Foundational Claim' : 'Derived Claim';
    case 'phenomenon': {
      const p = el as PhenomenonElement;
      const map = {
        foundational: 'Metaphysical Phenomenon',
        qm: 'Quantum-Mechanical Phenomenon',
        modal: 'Modal Phenomenon',
        agency: 'Agency Phenomenon',
        temporal: 'Temporal Phenomenon',
      } as const;
      return map[p.domain];
    }
    case 'paper': {
      const p = el as PaperElement;
      const map = {
        published: 'Published Paper',
        review: 'Paper Under Review',
        progress: 'Paper In Progress',
        planned: 'Planned Paper',
      } as const;
      return map[p.status];
    }
    case 'domain':
      return 'Domain of the Unification Field';
    case 'feature':
      return 'The Unifying Feature';
  }
}

function metaFor(el: SystemElement): { key: string; val: string }[] {
  const out: { key: string; val: string }[] = [];
  if (el.kind === 'paper') {
    const p = el as PaperElement;
    if (p.venue) out.push({ key: 'Venue', val: p.venue });
    if (p.authors) out.push({ key: 'Authors', val: p.authors });
  }
  if (el.kind === 'domain') {
    const d = el as DomainElement;
    out.push({ key: 'Standing positions', val: d.positions.join(' · ') });
  }
  if (el.kind === 'concept') {
    const c = el as ConceptElement;
    if (c.role) out.push({ key: 'Role', val: c.role });
  }
  return out;
}

export class DetailPanel {
  private root: HTMLElement;
  private currentId: string | null = null;
  onClose?: () => void;
  onTagClick?: (id: string) => void;

  constructor(rootSelector = '#detail-root') {
    const root = document.querySelector<HTMLElement>(rootSelector);
    if (!root) throw new Error('detail-root not found');
    this.root = root;
    this.render();

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.currentId) this.close();
    });
  }

  private render() {
    this.root.innerHTML = `
      <div class="detail-scrim"></div>
      <article class="detail-panel" role="dialog" aria-modal="true" aria-hidden="true">
        <button class="close" aria-label="Close">×</button>
        <div class="d-eyebrow">
          <span class="num"></span>
          <span class="kind"></span>
        </div>
        <h2 class="d-title"></h2>
        <p class="d-subtitle"></p>
        <div class="d-rule"></div>
        <p class="d-section-label">Description</p>
        <div class="d-body"></div>
        <p class="d-section-label related-label">Related</p>
        <div class="d-related"></div>
        <div class="d-rule"></div>
        <button class="d-link">Read full page →</button>
        <div class="d-meta"></div>
      </article>
    `;
    this.root.querySelector<HTMLElement>('.detail-scrim')!
      .addEventListener('click', () => this.close());
    this.root.querySelector<HTMLElement>('.close')!
      .addEventListener('click', () => this.close());
    this.root.querySelector<HTMLElement>('.d-link')!
      .addEventListener('click', () => {
        if (this.currentId) {
          const el = BY_ID[this.currentId];
          const slug = SLUG_BY_KIND[el.kind];
          // eslint-disable-next-line no-console
          console.log(`Would navigate to /${slug}/${el.id}`);
        }
      });
  }

  open(id: string) {
    const el = BY_ID[id];
    if (!el) return;
    this.currentId = id;
    const panel = this.root.querySelector<HTMLElement>('.detail-panel')!;

    panel.querySelector<HTMLElement>('.num')!.textContent = el.index;
    panel.querySelector<HTMLElement>('.kind')!.textContent = eyebrowFor(el);
    panel.querySelector<HTMLElement>('.d-title')!.textContent = el.title;
    const subtitle = panel.querySelector<HTMLElement>('.d-subtitle')!;
    if (el.subtitle) {
      subtitle.textContent = el.subtitle;
      subtitle.style.display = '';
    } else {
      subtitle.style.display = 'none';
    }

    const body = panel.querySelector<HTMLElement>('.d-body')!;
    body.innerHTML = el.body.map((p) => `<p>${p}</p>`).join('');

    const related = panel.querySelector<HTMLElement>('.d-related')!;
    const relatedLabel = panel.querySelector<HTMLElement>('.related-label')!;
    related.innerHTML = '';
    if (el.related.length === 0) {
      relatedLabel.style.display = 'none';
      related.style.display = 'none';
    } else {
      relatedLabel.style.display = '';
      related.style.display = '';
      for (const rid of el.related) {
        const rel = BY_ID[rid];
        if (!rel) continue;
        const tag = document.createElement('button');
        tag.className = 'd-tag';
        tag.textContent = rel.title;
        tag.addEventListener('click', () => this.onTagClick?.(rid));
        related.appendChild(tag);
      }
    }

    const meta = panel.querySelector<HTMLElement>('.d-meta')!;
    meta.innerHTML = metaFor(el).map((m) =>
      `<div><span class="key">${m.key}</span><span class="val">${m.val}</span></div>`
    ).join('');

    this.root.classList.add('open');
    panel.setAttribute('aria-hidden', 'false');
  }

  close() {
    if (!this.currentId) return;
    this.currentId = null;
    this.root.classList.remove('open');
    this.root.querySelector<HTMLElement>('.detail-panel')!.setAttribute('aria-hidden', 'true');
    this.onClose?.();
  }

  isOpen(): boolean { return this.currentId !== null; }
  get id(): string | null { return this.currentId; }
}
